import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Collapse,
  useTheme,
  CircularProgress,
  TextField,
  Autocomplete,
  Chip,
  IconButton,
  Button,
} from "@mui/material";
import { keyframes } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SprintIcon from "../Recipes/assets/sprint.svg";
import PersonIcon from "@mui/icons-material/Person";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import supabase from "../supabaseClient";
import UserContext from "../contexts/userContext";

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const TIME_SUGGESTIONS = [
  { label: "15 min", value: "15" },
  { label: "30 min", value: "30" },
  { label: "60 min", value: "60" },
  { label: "120 min", value: "120" },
];

function QuickChip({ label, active, onClick }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accentPrimary = theme.palette.accent?.primary || theme.palette.primary.main;
  
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 28,
        px: 1.5,
        borderRadius: "8px",
        fontFamily: theme.typography.fontFamily,
        fontSize: "0.75rem",
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        backgroundColor: active
          ? isDark 
            ? `${accentPrimary}25`
            : `${accentPrimary}15`
          : isDark 
            ? "rgba(255,255,255,0.06)"
            : "rgba(0,0,0,0.04)",
        color: active 
          ? accentPrimary 
          : theme.palette.text.primary,
        border: `1px solid ${active 
          ? isDark ? `${accentPrimary}50` : `${accentPrimary}40`
          : "transparent"
        }`,
        "&:hover": {
          backgroundColor: active
            ? isDark 
              ? `${accentPrimary}35`
              : `${accentPrimary}20`
            : isDark 
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.06)",
          transform: "translateY(-1px)",
        },
        "&:active": { 
          transform: "scale(0.97)",
        },
      }}
    >
      {label}
    </Box>
  );
}

function FilterSection({ title, icon: Icon, iconSrc, children, isOpen, onToggle, count = 0, onClear }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accentPrimary = theme.palette.accent?.primary || theme.palette.primary.main;

  return (
    <Box 
      sx={{ 
        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1.25,
          px: 1.5,
          cursor: "pointer",
          transition: "all 0.15s ease",
          "&:hover": { 
            backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "8px",
            backgroundColor: count > 0 
              ? isDark ? `${accentPrimary}20` : `${accentPrimary}12`
              : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
            transition: "all 0.2s ease",
          }}
        >
          {iconSrc ? (
            <img 
              src={iconSrc} 
              alt="" 
              style={{ 
                width: 15, 
                height: 15,
                filter: isDark 
                  ? 'brightness(0) invert(1) opacity(0.38)' 
                  : 'brightness(0) invert(0.55)',
              }} 
            />
          ) : (
            <Icon 
              sx={{ 
                fontSize: 15, 
                color: count > 0 ? accentPrimary : theme.palette.text.secondary,
                transition: "color 0.2s ease",
              }} 
            />
          )}
        </Box>
        <Typography 
          sx={{ 
            fontFamily: theme.typography.fontFamily, 
            fontWeight: 500, 
            fontSize: "0.8rem", 
            flex: 1,
            color: count > 0 ? theme.palette.text.primary : theme.palette.text.secondary,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </Typography>
        {count > 0 && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 20,
                height: 20,
                px: 0.5,
                borderRadius: "6px",
                backgroundColor: isDark ? `${accentPrimary}25` : `${accentPrimary}15`,
                color: accentPrimary,
                fontSize: "0.7rem",
                fontWeight: 600,
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {count}
            </Box>
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); onClear?.(); }} 
              sx={{ 
                p: 0.25,
                opacity: 0.6,
                "&:hover": { opacity: 1, backgroundColor: "transparent" },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </>
        )}
        <ExpandMoreIcon 
          sx={{ 
            fontSize: 18, 
            color: theme.palette.text.secondary, 
            transform: isOpen ? "rotate(180deg)" : "rotate(0)", 
            transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            opacity: 0.6,
          }} 
        />
      </Box>
      <Collapse in={isOpen}>
        <Box 
          sx={{ 
            px: 2, 
            pb: 1.5, 
            pt: 0.5,
            animation: `${fadeIn} 0.2s ease-out`,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

// Shared input styles
const getInputStyles = (theme) => {
  const isDark = theme.palette.mode === "dark";
  const accentPrimary = theme.palette.accent?.primary || theme.palette.primary.main;
  
  return {
    "& .MuiOutlinedInput-root": {
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.8rem",
      borderRadius: "10px",
      backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      "& fieldset": {
        borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        borderWidth: "1px",
        transition: "border-color 0.2s ease",
      },
      "&:hover fieldset": {
        borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)",
      },
      "&.Mui-focused fieldset": {
        borderColor: accentPrimary,
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      py: 1,
      px: 1.25,
      "&::placeholder": {
        fontSize: "0.75rem",
        opacity: 0.6,
      },
    },
  };
};

function FilterFaceted({ 
  selectedFilters = [], 
  updateFilters, 
  query = "", 
  isOpen: controlledIsOpen, 
  onToggle,
  compact = false,
  placement = "row"
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accentPrimary = theme.palette.accent?.primary || theme.palette.primary.main;
  // eslint-disable-next-line no-unused-vars
  const { user } = useContext(UserContext);

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const handleToggle = onToggle || (() => setInternalIsOpen(!internalIsOpen));

  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState({ books: [], authors: [], tags: [] });
  const [customInputs, setCustomInputs] = useState({ time: "", book: "", author: "", tag: "" });
  
  const [stagedFilters, setStagedFilters] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [openSection, setOpenSection] = useState("time");

  useEffect(() => {
    if (isOpen) {
      setStagedFilters([...selectedFilters]);
      setHasChanges(false);
    }
  }, [isOpen, selectedFilters]);

  useEffect(() => {
    if (suggestions.books.length > 0) return;

    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const { data: booksData } = await supabase.from("books").select("name");
        const { data: authorsData } = await supabase.from("books").select("author").not("author", "is", null);
        const { data: tagsData } = await supabase.from("tags").select("name, category:tags_categories(name)").eq("is_public", true);

        const uniqueBooks = booksData ? [...new Set(booksData.map((b) => b.name))].sort() : [];
        const uniqueAuthors = authorsData ? [...new Set(authorsData.map((a) => a.author))].sort() : [];

        setSuggestions({ books: uniqueBooks, authors: uniqueAuthors, tags: tagsData || [] });
      } catch (err) {
        console.error("Error fetching suggestions:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [suggestions.books.length]);

  const getStagedFilters = (type) => stagedFilters.filter((f) => f.type === type).map((f) => f.value);
  const stagedTimeFilter = stagedFilters.find((f) => f.type === "Time");
  const stagedBooks = getStagedFilters("Book");
  const stagedAuthors = getStagedFilters("Author");
  const stagedTags = getStagedFilters("Tag");
  const totalActiveFilters = selectedFilters.length;
  const totalStagedFilters = stagedFilters.length;

  const updateStaged = (type, value, remove = false) => {
    setHasChanges(true);
    setStagedFilters((prev) => {
      if (type === "Time") {
        const without = prev.filter((f) => f.type !== "Time");
        return remove || !value ? without : [...without, { type: "Time", value }];
      }
      if (remove) {
        return prev.filter((f) => !(f.type === type && f.value === value));
      }
      if (prev.some((f) => f.type === type && f.value === value)) {
        return prev;
      }
      return [...prev, { type, value }];
    });
  };

  const handleTimeChip = (value) => {
    if (stagedTimeFilter?.value === value) {
      updateStaged("Time", "", true);
    } else {
      updateStaged("Time", value);
    }
  };

  const handleCustomTime = () => {
    const val = customInputs.time.trim();
    if (val && !isNaN(val)) {
      updateStaged("Time", val);
      setCustomInputs((p) => ({ ...p, time: "" }));
    }
  };

  const handleChipToggle = (type, value) => {
    const isActive = getStagedFilters(type).includes(value);
    updateStaged(type, value, isActive);
  };

  const clearStagedType = (type) => {
    setHasChanges(true);
    setStagedFilters((prev) => prev.filter((f) => f.type !== type));
  };

  const applyFilters = () => {
    selectedFilters.forEach((f) => {
      if (f.type === "Time") {
        updateFilters("Time", "", true);
      } else {
        updateFilters(f.type, f.value, true);
      }
    });
    stagedFilters.forEach((f) => {
      updateFilters(f.type, f.value);
    });
    setHasChanges(false);
    setInternalIsOpen(false);
  };

  const cancelChanges = () => {
    setStagedFilters([...selectedFilters]);
    setHasChanges(false);
    if (onToggle) {
      onToggle();
    } else {
      setInternalIsOpen(false);
    }
  };

  const topBooks = suggestions.books.slice(0, 5);
  const topAuthors = suggestions.authors.slice(0, 5);
  const topTags = suggestions.tags.slice(0, 6).map((t) => t.name);

  // Glass container styles
  const glassContainerStyles = {
    background: isDark
      ? "linear-gradient(135deg, rgba(30, 30, 35, 0.85) 0%, rgba(20, 20, 25, 0.9) 100%)"
      : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
    borderRadius: "16px",
    boxShadow: isDark
      ? "0 8px 32px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)"
      : "0 8px 32px -8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.8)",
    overflow: "hidden",
  };

  // Selected chips styles
  const selectedChipStyles = {
    height: 24,
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.7rem",
    fontWeight: 500,
    backgroundColor: isDark ? `${accentPrimary}20` : `${accentPrimary}12`,
    color: accentPrimary,
    border: `1px solid ${isDark ? `${accentPrimary}40` : `${accentPrimary}30`}`,
    "& .MuiChip-deleteIcon": { 
      fontSize: 14,
      color: accentPrimary,
      opacity: 0.7,
      "&:hover": { opacity: 1 },
    },
  };

  const renderFilterContent = () => (
    <>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} sx={{ color: accentPrimary }} />
        </Box>
      ) : (
        <>
          {/* Time Section */}
          <FilterSection 
            title="active time" 
            iconSrc={SprintIcon} 
            isOpen={openSection === "time"}
            onToggle={() => setOpenSection(openSection === "time" ? null : "time")}
            count={stagedTimeFilter ? 1 : 0} 
            onClear={() => clearStagedType("Time")}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
              {TIME_SUGGESTIONS.map((t) => (
                <QuickChip 
                  key={t.value} 
                  label={t.label} 
                  active={stagedTimeFilter?.value === t.value} 
                  onClick={() => handleTimeChip(t.value)} 
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="enter time"
                type="number"
                value={customInputs.time}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^\d+$/.test(val)) {
                    setCustomInputs((p) => ({ ...p, time: val }));
                  }
                }}
                onKeyDown={(e) => e.key === "Enter" && handleCustomTime()}
                InputProps={{
                  endAdornment: (
                    <Typography 
                      sx={{ 
                        fontSize: "0.75rem", 
                        color: theme.palette.text.secondary,
                        whiteSpace: "nowrap",
                        ml: 0.5,
                      }}
                    >
                      minutes
                    </Typography>
                  ),
                  inputProps: { min: 1 }
                }}
                sx={{ 
                  flex: 1, 
                  ...getInputStyles(theme),
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
              <IconButton 
                size="small" 
                onClick={handleCustomTime} 
                disabled={!customInputs.time.trim()}
                sx={{ 
                  p: 0.75,
                  borderRadius: "8px",
                  backgroundColor: customInputs.time.trim() 
                    ? isDark ? `${accentPrimary}20` : `${accentPrimary}12`
                    : "transparent",
                  color: customInputs.time.trim() ? accentPrimary : theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: isDark ? `${accentPrimary}30` : `${accentPrimary}18`,
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </FilterSection>

          {/* Sources Section */}
          <FilterSection 
            title="sources" 
            icon={MenuBookIcon} 
            isOpen={openSection === "book"}
            onToggle={() => setOpenSection(openSection === "book" ? null : "book")}
            count={stagedBooks.length} 
            onClear={() => clearStagedType("Book")}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
              {topBooks.map((book) => (
                <QuickChip 
                  key={book} 
                  label={book.length > 18 ? book.slice(0, 16) + "…" : book} 
                  active={stagedBooks.includes(book)} 
                  onClick={() => handleChipToggle("Book", book)} 
                />
              ))}
            </Box>
            <Autocomplete
              size="small"
              options={suggestions.books.filter((b) => !stagedBooks.includes(b))}
              inputValue={customInputs.book}
              onInputChange={(_, val) => setCustomInputs((p) => ({ ...p, book: val }))}
              onChange={(_, val) => { if (val) { updateStaged("Book", val); setCustomInputs((p) => ({ ...p, book: "" })); } }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  placeholder="search sources..." 
                  sx={getInputStyles(theme)}
                />
              )}
              slotProps={{ 
                paper: { 
                  sx: { 
                    ...glassContainerStyles,
                    mt: 0.5,
                    fontSize: "0.8rem", 
                    fontFamily: theme.typography.fontFamily,
                  } 
                }, 
                listbox: { 
                  sx: { 
                    py: 0.5, 
                    "& .MuiAutocomplete-option": { 
                      minHeight: 36, 
                      py: 0.75, 
                      px: 1.5,
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      mx: 0.5,
                      "&:hover": {
                        backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                      },
                    } 
                  } 
                } 
              }}
            />
            {stagedBooks.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
                {stagedBooks.map((book) => (
                  <Chip 
                    key={book} 
                    label={book} 
                    size="small" 
                    onDelete={() => updateStaged("Book", book, true)} 
                    sx={selectedChipStyles}
                  />
                ))}
              </Box>
            )}
          </FilterSection>

          {/* Authors Section */}
          <FilterSection 
            title="authors" 
            icon={PersonIcon} 
            isOpen={openSection === "author"}
            onToggle={() => setOpenSection(openSection === "author" ? null : "author")}
            count={stagedAuthors.length} 
            onClear={() => clearStagedType("Author")}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
              {topAuthors.map((author) => (
                <QuickChip 
                  key={author} 
                  label={author.length > 16 ? author.slice(0, 14) + "…" : author} 
                  active={stagedAuthors.includes(author)} 
                  onClick={() => handleChipToggle("Author", author)} 
                />
              ))}
            </Box>
            <Autocomplete
              size="small"
              options={suggestions.authors.filter((a) => !stagedAuthors.includes(a))}
              inputValue={customInputs.author}
              onInputChange={(_, val) => setCustomInputs((p) => ({ ...p, author: val }))}
              onChange={(_, val) => { if (val) { updateStaged("Author", val); setCustomInputs((p) => ({ ...p, author: "" })); } }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  placeholder="search authors..." 
                  sx={getInputStyles(theme)}
                />
              )}
              slotProps={{ 
                paper: { 
                  sx: { 
                    ...glassContainerStyles,
                    mt: 0.5,
                    fontSize: "0.8rem", 
                    fontFamily: theme.typography.fontFamily,
                  } 
                }, 
                listbox: { 
                  sx: { 
                    py: 0.5, 
                    "& .MuiAutocomplete-option": { 
                      minHeight: 36, 
                      py: 0.75, 
                      px: 1.5,
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      mx: 0.5,
                    } 
                  } 
                } 
              }}
            />
            {stagedAuthors.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
                {stagedAuthors.map((author) => (
                  <Chip 
                    key={author} 
                    label={author} 
                    size="small" 
                    onDelete={() => updateStaged("Author", author, true)} 
                    sx={selectedChipStyles}
                  />
                ))}
              </Box>
            )}
          </FilterSection>

          {/* Tags Section */}
          <FilterSection 
            title="tags" 
            icon={LocalOfferIcon} 
            isOpen={openSection === "tag"}
            onToggle={() => setOpenSection(openSection === "tag" ? null : "tag")}
            count={stagedTags.length} 
            onClear={() => clearStagedType("Tag")}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
              {topTags.map((tag) => (
                <QuickChip 
                  key={tag} 
                  label={tag} 
                  active={stagedTags.includes(tag)} 
                  onClick={() => handleChipToggle("Tag", tag)} 
                />
              ))}
            </Box>
            <Autocomplete
              size="small"
              options={suggestions.tags.filter((t) => !stagedTags.includes(t.name))}
              groupBy={(option) => (option.category && option.category.name) || "Uncategorized"}
              getOptionLabel={(option) => option.name}
              inputValue={customInputs.tag}
              onInputChange={(_, val) => setCustomInputs((p) => ({ ...p, tag: val }))}
              onChange={(_, val) => { 
                if (val) { 
                  updateStaged("Tag", val.name); 
                  setCustomInputs((p) => ({ ...p, tag: "" })); 
                } 
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  placeholder="search tags..." 
                  sx={getInputStyles(theme)}
                />
              )}
              slotProps={{ 
                paper: { 
                  sx: { 
                    ...glassContainerStyles,
                    mt: 0.5,
                    fontSize: "0.8rem", 
                    fontFamily: theme.typography.fontFamily,
                  } 
                }, 
                listbox: { 
                  sx: { 
                    py: 0.5, 
                    "& .MuiAutocomplete-groupLabel": {
                      fontFamily: theme.typography.fontFamily,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: accentPrimary,
                      textTransform: "lowercase",
                      letterSpacing: "0.02em",
                      backgroundColor: isDark ? "rgba(25, 25, 30, 1)" : "rgba(250, 250, 252, 1)",
                      px: 1.5,
                      py: 0.4,
                      position: "sticky",
                      top: -4,
                      zIndex: 1,
                      marginTop: 0,
                    },
                    "& .MuiAutocomplete-option": { 
                      minHeight: 36, 
                      py: 0.75, 
                      px: 1.5,
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      mx: 0.5,
                    } 
                  } 
                } 
              }}
            />
            {stagedTags.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
                {stagedTags.map((tag) => (
                  <Chip 
                    key={tag} 
                    label={tag} 
                    size="small" 
                    onDelete={() => updateStaged("Tag", tag, true)} 
                    sx={selectedChipStyles}
                  />
                ))}
              </Box>
            )}
          </FilterSection>

          {/* Apply/Cancel buttons */}
          <Box 
            sx={{ 
              display: "flex", 
              gap: 1, 
              p: 1.5, 
              pt: 1.25,
              borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
              background: isDark 
                ? "linear-gradient(to top, rgba(0,0,0,0.2) 0%, transparent 100%)"
                : "linear-gradient(to top, rgba(0,0,0,0.02) 0%, transparent 100%)",
            }}
          >
            <Button
              size="small"
              onClick={cancelChanges}
              disabled={!hasChanges}
              sx={{ 
                flex: 1, 
                textTransform: "none", 
                fontFamily: theme.typography.fontFamily, 
                fontSize: "0.8rem",
                fontWeight: 500,
                py: 0.75,
                borderRadius: "10px",
                color: theme.palette.text.secondary,
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                "&:hover": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                  borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)",
                },
                "&:disabled": {
                  opacity: 0.4,
                  borderColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              cancel
            </Button>
            <Button
              size="small"
              onClick={applyFilters}
              sx={{ 
                flex: 1, 
                textTransform: "none", 
                fontFamily: theme.typography.fontFamily, 
                fontSize: "0.8rem",
                fontWeight: 600,
                py: 0.75,
                borderRadius: "10px",
                backgroundColor: accentPrimary,
                color: isDark ? "#121212" : "#FFFFFF",
                boxShadow: `0 2px 8px -2px ${accentPrimary}60`,
                "&:hover": {
                  backgroundColor: accentPrimary,
                  boxShadow: `0 4px 12px -2px ${accentPrimary}80`,
                },
              }}
            >
              apply {totalStagedFilters > 0 && `(${totalStagedFilters})`}
            </Button>
          </Box>
        </>
      )}
    </>
  );

  // FAB placement
  if (placement === "fab") {
    return (
      <>
        <IconButton
          onClick={handleToggle}
          sx={{
            position: "fixed",
            bottom: 80,
            right: 16,
            zIndex: 1200,
            backgroundColor: totalActiveFilters > 0 
              ? accentPrimary 
              : isDark ? "rgba(30, 30, 35, 0.9)" : "rgba(255, 255, 255, 0.95)",
            color: totalActiveFilters > 0 
              ? (isDark ? "#121212" : "#FFFFFF")
              : theme.palette.text.primary,
            boxShadow: isDark
              ? "0 4px 20px rgba(0,0,0,0.5)"
              : "0 4px 20px rgba(0,0,0,0.15)",
            backdropFilter: "blur(10px)",
            width: 52,
            height: 52,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: totalActiveFilters > 0 
                ? accentPrimary 
                : isDark ? "rgba(40, 40, 45, 0.95)" : "rgba(255, 255, 255, 1)",
              transform: "scale(1.05)",
            },
          }}
        >
          {totalActiveFilters > 0 ? (
            <Box sx={{ position: "relative" }}>
              <TuneIcon />
              <Box
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: isDark ? "#121212" : "#FFFFFF",
                  color: accentPrimary,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                {totalActiveFilters}
              </Box>
            </Box>
          ) : (
            <TuneIcon />
          )}
        </IconButton>
        <Collapse 
          in={isOpen} 
          sx={{ 
            position: "fixed", 
            bottom: 140, 
            right: 16, 
            zIndex: 1200, 
            maxWidth: 340, 
            width: "calc(100vw - 32px)" 
          }}
        >
          <Box sx={{ ...glassContainerStyles, animation: `${slideDown} 0.25s ease-out` }}>
            {renderFilterContent()}
          </Box>
        </Collapse>
      </>
    );
  }

  // Compact mode - just icon button
  if (compact) {
    return (
      <IconButton
        size="small"
        onClick={handleToggle}
        sx={{
          p: 0.75,
          borderRadius: "10px",
          backgroundColor: isOpen || totalActiveFilters > 0 
            ? isDark ? `${accentPrimary}20` : `${accentPrimary}12`
            : "transparent",
          border: `1px solid ${isOpen || totalActiveFilters > 0 
            ? isDark ? `${accentPrimary}40` : `${accentPrimary}30`
            : "transparent"
          }`,
          transition: "all 0.2s ease",
          "&:hover": { 
            backgroundColor: isDark ? `${accentPrimary}25` : `${accentPrimary}15`,
          },
        }}
      >
        {totalActiveFilters > 0 ? (
          <Box sx={{ position: "relative", display: "flex" }}>
            <TuneIcon 
              sx={{ 
                fontSize: 18, 
                color: accentPrimary,
              }} 
            />
            <Box
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: accentPrimary,
                color: isDark ? "#121212" : "#FFFFFF",
                fontSize: "0.55rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {totalActiveFilters}
            </Box>
          </Box>
        ) : (
          <TuneIcon 
            sx={{ 
              fontSize: 18, 
              color: isOpen ? accentPrimary : theme.palette.text.secondary,
            }} 
          />
        )}
      </IconButton>
    );
  }

  // Default row placement
  return (
    <Box>
      <Button
        size="small"
        startIcon={<TuneIcon sx={{ fontSize: 15 }} />}
        onClick={handleToggle}
        sx={{
          textTransform: "none",
          fontFamily: theme.typography.fontFamily,
          fontSize: "0.8rem",
          fontWeight: totalActiveFilters > 0 ? 600 : 500,
          color: totalActiveFilters > 0 ? accentPrimary : theme.palette.text.secondary,
          px: 1.25,
          py: 0.5,
          borderRadius: "10px",
          backgroundColor: isOpen 
            ? isDark ? `${accentPrimary}15` : `${accentPrimary}10`
            : "transparent",
          border: `1px solid ${isOpen || totalActiveFilters > 0
            ? isDark ? `${accentPrimary}30` : `${accentPrimary}25`
            : "transparent"
          }`,
          transition: "all 0.2s ease",
          "&:hover": { 
            backgroundColor: isDark ? `${accentPrimary}20` : `${accentPrimary}12`,
          },
        }}
      >
        filters
        {totalActiveFilters > 0 && (
          <Box
            sx={{
              ml: 0.75,
              width: 18,
              height: 18,
              borderRadius: "6px",
              backgroundColor: accentPrimary,
              color: isDark ? "#121212" : "#FFFFFF",
              fontSize: "0.65rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: theme.typography.fontFamily,
            }}
          >
            {totalActiveFilters}
          </Box>
        )}
      </Button>
      <Collapse in={isOpen}>
        <Box 
          sx={{ 
            mt: 1.5, 
            animation: `${slideDown} 0.25s ease-out`, 
            ...glassContainerStyles,
          }}
        >
          {renderFilterContent()}
        </Box>
      </Collapse>
    </Box>
  );
}

// Separate panel component for inline rendering below tabs
export function FilterPanel({ isOpen, selectedFilters, updateFilters, query, onClose }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accentPrimary = theme.palette.accent?.primary || theme.palette.primary.main;
  // eslint-disable-next-line no-unused-vars
  const { user } = useContext(UserContext);
  
  const [openSection, setOpenSection] = useState("time");
  const [books, setBooks] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [stagedTimeFilter, setStagedTimeFilter] = useState(null);
  const [stagedBooks, setStagedBooks] = useState([]);
  const [stagedAuthors, setStagedAuthors] = useState([]);
  const [stagedTags, setStagedTags] = useState([]);

  // Glass container styles
  const glassContainerStyles = {
    background: isDark
      ? "linear-gradient(135deg, rgba(30, 30, 35, 0.85) 0%, rgba(20, 20, 25, 0.9) 100%)"
      : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
    borderRadius: "16px",
    boxShadow: isDark
      ? "0 8px 32px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)"
      : "0 8px 32px -8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.8)",
    overflow: "hidden",
  };

  // Selected chips styles
  const selectedChipStyles = {
    height: 24,
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.7rem",
    fontWeight: 500,
    backgroundColor: isDark ? `${accentPrimary}20` : `${accentPrimary}12`,
    color: accentPrimary,
    border: `1px solid ${isDark ? `${accentPrimary}40` : `${accentPrimary}30`}`,
    "& .MuiChip-deleteIcon": { 
      fontSize: 14,
      color: accentPrimary,
      opacity: 0.7,
      "&:hover": { opacity: 1 },
    },
  };

  useEffect(() => {
    const timeFilter = selectedFilters.find(f => f.type === "Time");
    setStagedTimeFilter(timeFilter ? timeFilter.value : null);
    setStagedBooks(selectedFilters.filter(f => f.type === "Book").map(f => f.value));
    setStagedAuthors(selectedFilters.filter(f => f.type === "Author").map(f => f.value));
    setStagedTags(selectedFilters.filter(f => f.type === "Tag").map(f => f.value));
  }, [selectedFilters, isOpen]);

  useEffect(() => {
    if (books.length > 0) return;
    
    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const [booksRes, authorsRes, tagsRes] = await Promise.all([
          supabase.from("books").select("name"),
          supabase.from("books").select("author").not("author", "is", null),
          supabase.from("tags").select("name, category:tags_categories(name)").eq("is_public", true),
        ]);

        const uniqueBooks = booksRes.data ? [...new Set(booksRes.data.map(r => r.name).filter(Boolean))].sort() : [];
        const uniqueAuthors = authorsRes.data ? [...new Set(authorsRes.data.map(r => r.author).filter(Boolean))].sort() : [];

        setBooks(uniqueBooks);
        setAuthors(uniqueAuthors);
        setTags(tagsRes.data || []);
      } catch (error) {
        console.error("Error fetching filter suggestions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [books.length]);

  const handleApply = () => {
    const newFilters = [];
    if (stagedTimeFilter) {
      newFilters.push({ type: "Time", value: stagedTimeFilter, label: `< ${stagedTimeFilter}m` });
    }
    stagedBooks.forEach(book => {
      newFilters.push({ type: "Book", value: book, label: book });
    });
    stagedAuthors.forEach(author => {
      newFilters.push({ type: "Author", value: author, label: author });
    });
    stagedTags.forEach(tag => {
      newFilters.push({ type: "Tag", value: tag, label: tag });
    });
    updateFilters(newFilters);
  };

  const handleCancel = () => {
    const timeFilter = selectedFilters.find(f => f.type === "Time");
    setStagedTimeFilter(timeFilter ? timeFilter.value : null);
    setStagedBooks(selectedFilters.filter(f => f.type === "Book").map(f => f.value));
    setStagedAuthors(selectedFilters.filter(f => f.type === "Author").map(f => f.value));
    setStagedTags(selectedFilters.filter(f => f.type === "Tag").map(f => f.value));
    onClose?.();
  };

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const hasChanges = () => {
    const currentTime = selectedFilters.find(f => f.type === "Time")?.value || null;
    const currentBooks = selectedFilters.filter(f => f.type === "Book").map(f => f.value);
    const currentAuthors = selectedFilters.filter(f => f.type === "Author").map(f => f.value);
    const currentTags = selectedFilters.filter(f => f.type === "Tag").map(f => f.value);
    
    return stagedTimeFilter !== currentTime ||
      JSON.stringify(stagedBooks.sort()) !== JSON.stringify(currentBooks.sort()) ||
      JSON.stringify(stagedAuthors.sort()) !== JSON.stringify(currentAuthors.sort()) ||
      JSON.stringify(stagedTags.sort()) !== JSON.stringify(currentTags.sort());
  };

  return (
    <Collapse in={isOpen} sx={{ position: "relative", zIndex: 10 }}>
      <Box 
        sx={{ 
          mt: 1.5, 
          mb: 1,
          animation: `${slideDown} 0.25s ease-out`, 
          ...glassContainerStyles,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} sx={{ color: accentPrimary }} />
          </Box>
        ) : (
          <>
            {/* Time Section */}
            <FilterSection
              title="active time"
              iconSrc={SprintIcon}
              isOpen={openSection === "time"}
              onToggle={() => toggleSection("time")}
              count={stagedTimeFilter ? 1 : 0}
              onClear={() => setStagedTimeFilter(null)}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
                {TIME_SUGGESTIONS.map((time) => (
                  <QuickChip
                    key={time.value}
                    label={time.label}
                    active={stagedTimeFilter === time.value}
                    onClick={() => setStagedTimeFilter(stagedTimeFilter === time.value ? null : time.value)}
                  />
                ))}
              </Box>
              <TextField
                size="small"
                placeholder="enter time"
                type="number"
                value={stagedTimeFilter && !TIME_SUGGESTIONS.find(t => t.value === stagedTimeFilter) ? stagedTimeFilter : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^\d+$/.test(val)) {
                    setStagedTimeFilter(val || null);
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <Typography 
                      sx={{ 
                        fontSize: "0.75rem", 
                        color: theme.palette.text.secondary,
                        whiteSpace: "nowrap",
                        ml: 0.5,
                      }}
                    >
                      minutes
                    </Typography>
                  ),
                  inputProps: { min: 1 }
                }}
                sx={{
                  ...getInputStyles(theme),
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
            </FilterSection>

            {/* Sources Section */}
            <FilterSection
              title="sources"
              icon={MenuBookIcon}
              isOpen={openSection === "books"}
              onToggle={() => toggleSection("books")}
              count={stagedBooks.length}
              onClear={() => setStagedBooks([])}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
                {books.slice(0, 8).map((book) => (
                  <QuickChip
                    key={book}
                    label={book.length > 18 ? book.slice(0, 16) + "…" : book}
                    active={stagedBooks.includes(book)}
                    onClick={() => setStagedBooks(prev => 
                      prev.includes(book) ? prev.filter(b => b !== book) : [...prev, book]
                    )}
                  />
                ))}
              </Box>
              <Autocomplete
                size="small"
                options={books}
                value={null}
                onChange={(e, value) => {
                  if (value && !stagedBooks.includes(value)) {
                    setStagedBooks(prev => [...prev, value]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="search sources..."
                    size="small"
                    sx={getInputStyles(theme)}
                  />
                )}
                slotProps={{
                  paper: { 
                    sx: { 
                      ...glassContainerStyles,
                      mt: 0.5,
                      fontSize: "0.8rem", 
                      fontFamily: theme.typography.fontFamily,
                    } 
                  },
                  listbox: { 
                    sx: { 
                      py: 0.5, 
                      "& .MuiAutocomplete-option": { 
                        minHeight: 36, 
                        py: 0.75, 
                        px: 1.5,
                        fontSize: "0.8rem",
                        borderRadius: "8px",
                        mx: 0.5,
                      } 
                    } 
                  }
                }}
              />
              {stagedBooks.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
                  {stagedBooks.map(book => (
                    <Chip
                      key={book}
                      label={book.length > 15 ? book.slice(0, 15) + "…" : book}
                      size="small"
                      onDelete={() => setStagedBooks(prev => prev.filter(b => b !== book))}
                      sx={selectedChipStyles}
                    />
                  ))}
                </Box>
              )}
            </FilterSection>

            {/* Authors Section */}
            <FilterSection
              title="authors"
              icon={PersonIcon}
              isOpen={openSection === "authors"}
              onToggle={() => toggleSection("authors")}
              count={stagedAuthors.length}
              onClear={() => setStagedAuthors([])}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
                {authors.slice(0, 8).map((author) => (
                  <QuickChip
                    key={author}
                    label={author.length > 16 ? author.slice(0, 14) + "…" : author}
                    active={stagedAuthors.includes(author)}
                    onClick={() => setStagedAuthors(prev => 
                      prev.includes(author) ? prev.filter(a => a !== author) : [...prev, author]
                    )}
                  />
                ))}
              </Box>
              <Autocomplete
                size="small"
                options={authors}
                value={null}
                onChange={(e, value) => {
                  if (value && !stagedAuthors.includes(value)) {
                    setStagedAuthors(prev => [...prev, value]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="search authors..."
                    size="small"
                    sx={getInputStyles(theme)}
                  />
                )}
                slotProps={{
                  paper: { 
                    sx: { 
                      ...glassContainerStyles,
                      mt: 0.5,
                      fontSize: "0.8rem", 
                      fontFamily: theme.typography.fontFamily,
                    } 
                  },
                  listbox: { 
                    sx: { 
                      py: 0.5, 
                      "& .MuiAutocomplete-option": { 
                        minHeight: 36, 
                        py: 0.75, 
                        px: 1.5,
                        fontSize: "0.8rem",
                        borderRadius: "8px",
                        mx: 0.5,
                      } 
                    } 
                  }
                }}
              />
              {stagedAuthors.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
                  {stagedAuthors.map(author => (
                    <Chip
                      key={author}
                      label={author.length > 15 ? author.slice(0, 15) + "…" : author}
                      size="small"
                      onDelete={() => setStagedAuthors(prev => prev.filter(a => a !== author))}
                      sx={selectedChipStyles}
                    />
                  ))}
                </Box>
              )}
            </FilterSection>

            {/* Tags Section */}
            <FilterSection
              title="tags"
              icon={LocalOfferIcon}
              isOpen={openSection === "tags"}
              onToggle={() => toggleSection("tags")}
              count={stagedTags.length}
              onClear={() => setStagedTags([])}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
                {tags.slice(0, 12).map((tag) => (
                  <QuickChip
                    key={tag.name}
                    label={tag.name}
                    active={stagedTags.includes(tag.name)}
                    onClick={() => setStagedTags(prev => 
                      prev.includes(tag.name) ? prev.filter(t => t !== tag.name) : [...prev, tag.name]
                    )}
                  />
                ))}
              </Box>
              <Autocomplete
                size="small"
                options={tags.filter((t) => !stagedTags.includes(t.name))}
                groupBy={(option) => (option.category && option.category.name) || "Uncategorized"}
                getOptionLabel={(option) => option.name}
                value={null}
                onChange={(e, value) => {
                  if (value) {
                    if (!stagedTags.includes(value.name)) {
                      setStagedTags(prev => [...prev, value.name]);
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="search tags..."
                    size="small"
                    sx={getInputStyles(theme)}
                  />
                )}
                slotProps={{
                  paper: { 
                    sx: { 
                      ...glassContainerStyles,
                      mt: 0.5,
                      fontSize: "0.8rem", 
                      fontFamily: theme.typography.fontFamily,
                    } 
                  },
                  listbox: { 
                    sx: { 
                      py: 0.5, 
                      "& .MuiAutocomplete-groupLabel": {
                        fontFamily: theme.typography.fontFamily,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: accentPrimary,
                        textTransform: "lowercase",
                        letterSpacing: "0.02em",
                        backgroundColor: isDark ? "rgba(25, 25, 30, 1)" : "rgba(250, 250, 252, 1)",
                        px: 1.5,
                        py: 0.4,
                        position: "sticky",
                        top: -4,
                        zIndex: 1,
                        marginTop: 0,
                      },
                      "& .MuiAutocomplete-option": { 
                        minHeight: 36, 
                        py: 0.75, 
                        px: 1.5,
                        fontSize: "0.8rem",
                        borderRadius: "8px",
                        mx: 0.5,
                      } 
                    } 
                  }
                }}
              />
              {stagedTags.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
                  {stagedTags.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={() => setStagedTags(prev => prev.filter(t => t !== tag))}
                      sx={selectedChipStyles}
                    />
                  ))}
                </Box>
              )}
            </FilterSection>

            {/* Action buttons */}
            <Box 
              sx={{ 
                display: "flex", 
                gap: 1, 
                p: 1.5, 
                pt: 1.25,
                borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
                background: isDark 
                  ? "linear-gradient(to top, rgba(0,0,0,0.2) 0%, transparent 100%)"
                  : "linear-gradient(to top, rgba(0,0,0,0.02) 0%, transparent 100%)",
              }}
            >
              <Button
                fullWidth
                size="small"
                onClick={handleCancel}
                sx={{ 
                  textTransform: "none", 
                  fontFamily: theme.typography.fontFamily,
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  py: 0.75,
                  borderRadius: "10px",
                  color: theme.palette.text.secondary,
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                    borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)",
                  },
                }}
              >
                cancel
              </Button>
              <Button
                fullWidth
                size="small"
                onClick={handleApply}
                disabled={!hasChanges()}
                sx={{ 
                  textTransform: "none", 
                  fontFamily: theme.typography.fontFamily,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  py: 0.75,
                  borderRadius: "10px",
                  backgroundColor: accentPrimary,
                  color: isDark ? "#121212" : "#FFFFFF",
                  boxShadow: `0 2px 8px -2px ${accentPrimary}60`,
                  "&:hover": {
                    backgroundColor: accentPrimary,
                    boxShadow: `0 4px 12px -2px ${accentPrimary}80`,
                  },
                  "&:disabled": {
                    backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)",
                    color: theme.palette.text.disabled,
                    boxShadow: "none",
                  },
                }}
              >
                apply
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Collapse>
  );
}

export default FilterFaceted;
